import { useEffect, useState, type DragEvent, type JSX } from 'react';
import type { CanonicalDocument, DocNode, InlineLine, NodeType } from '@cca/corpus-shared';
import { ApiError, api, type NewNodeInput, type NodePatchInput } from '../api/client';
import { LineEditor } from './LineEditor';
import { reorderIndex } from './block-reorder';

/**
 * Liturgy block editor (Commission only). The author works in plain fields —
 * line type + text + structure — never markup, ids, or numbers; rubric numbers
 * and note references recompute on the reading surface from structure alone.
 *
 * Every edit is optimistic: it applies to a local working copy instantly and
 * persists in the background (no per-edit refetch, nothing disabled), with a
 * snapshot-based Undo. Drops can land at any level (the block adopts the drop
 * target's nesting); inline bold/italic live in LineEditor.
 */

const EDITOR_TYPES: { v: NodeType; label: string }[] = [
  { v: 'heading', label: 'Header' },
  { v: 'rubric', label: 'Rubric' },
  { v: 'sub-rubric', label: 'Sub-rubric' },
  { v: 'spoken', label: 'Leader' },
  { v: 'response', label: 'Congregation' },
];

function editorType(t: NodeType): NodeType {
  if (t === 'spoken-block' || t === 'alternative') return 'spoken';
  if (t === 'prayer') return 'response';
  if (t === 'note') return 'heading';
  return t;
}
// Header/Rubric are top-level structural blocks — they can't be nested as a
// rubric's child line, so a drag that would do that snaps them back to top level.
const isStructural = (t: NodeType): boolean =>
  editorType(t) === 'heading' || editorType(t) === 'rubric';
// Left indent (rem). Headers are the outermost; everything else sits a step in,
// then nests by depth — so a Header reads slightly left of its Rubric.
const rowIndentRem = (t: NodeType, depth: number): number =>
  editorType(t) === 'heading' ? 0 : 0.9 + depth * 2.2;

const toLines = (text: string): InlineLine[] =>
  text.split('\n').map((line) => ({ runs: [{ text: line }] }));
const mid = (a: number, b: number) => (a + b) / 2;
const sameLines = (a: InlineLine[], b: InlineLine[]) => JSON.stringify(a) === JSON.stringify(b);

type DropAt = { id: string; pos: 'before' | 'after' };

interface BlockEditorProps {
  doc: CanonicalDocument;
}

export function BlockEditor({ doc }: BlockEditorProps): JSX.Element {
  const docId = doc.document.id;
  const [error, setError] = useState<string | null>(null);
  // Undo stack: each entry restores the prior local state instantly and reverts
  // the server in the background.
  const [undoStack, setUndoStack] = useState<
    { nodes: DocNode[]; revert: () => Promise<unknown> }[]
  >([]);
  const [dragId, setDragId] = useState<string | null>(null);
  // A row only becomes `draggable` once its handle is pressed (`armed`) —
  // otherwise draggable=true would hijack text selection in the editor.
  const [armed, setArmed] = useState<string | null>(null);
  // Live drop indicator: the row + side the dark insertion line is drawn on.
  const [dropAt, setDropAt] = useState<DropAt | null>(null);

  // Optimistic working copy — every edit applies here instantly and persists in
  // the background (no per-edit refetch, no global disable). We re-sync when the
  // server doc changes: first mount, and the reload on leaving edit mode.
  const [nodes, setNodes] = useState<DocNode[]>(doc.nodes);
  useEffect(() => setNodes(doc.nodes), [doc]);

  // Free-text notes (shown to all readers below the service) — a single
  // document-level field, persisted optimistically on blur.
  const [notes, setNotes] = useState<InlineLine[]>(doc.document.notes);
  useEffect(() => setNotes(doc.document.notes), [doc]);
  const saveNotes = (next: InlineLine[]) => {
    if (sameLines(next, notes)) return;
    const prev = notes;
    setError(null);
    setNotes(next);
    api.updateDocumentNotes(docId, next).catch((e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not save notes. Please try again.');
      setNotes(prev);
    });
  };

  const live = nodes.filter((n) => !n.deleted);
  const childrenOf = (pid: string | null) =>
    live.filter((n) => n.parentId === pid).sort((a, b) => a.orderIndex - b.orderIndex);

  const rows: { node: DocNode; depth: number }[] = [];
  const pushRows = (parentId: string | null, depth: number) => {
    for (const n of childrenOf(parentId)) {
      rows.push({ node: n, depth });
      pushRows(n.id, depth + 1);
    }
  };
  pushRows(null, 0);

  const fail = (e: unknown, prev: DocNode[]) => {
    setError(e instanceof ApiError ? e.message : 'Could not save. Please try again.');
    setNodes(prev);
  };
  const record = (prev: DocNode[], revert: () => Promise<unknown>) =>
    setUndoStack((s) => [...s, { nodes: prev, revert }]);

  // Apply a local transform now; persist op() in the background; record an Undo
  // (restore `prev` + revert the server) on success, or roll back on failure.
  const edit = (
    apply: (ns: DocNode[]) => DocNode[],
    op: () => Promise<unknown>,
    revert: () => Promise<unknown>,
  ) => {
    const prev = nodes;
    setError(null);
    setNodes(apply);
    op()
      .then(() => record(prev, revert))
      .catch((e: unknown) => fail(e, prev));
  };

  const undo = () => {
    const top = undoStack[undoStack.length - 1];
    if (!top) return;
    setUndoStack((s) => s.slice(0, -1));
    setError(null);
    setNodes(top.nodes);
    top
      .revert()
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Could not undo. Please reload.'),
      );
  };

  const setField = (ns: DocNode[], id: string, fields: Partial<DocNode>) =>
    ns.map((n) => (n.id === id ? { ...n, ...fields } : n));
  const patch = (id: string, fields: NodePatchInput) => api.updateNode(docId, id, fields);

  const saveLines = (node: DocNode, lines: InlineLine[]) => {
    if (sameLines(lines, node.lines)) return;
    const old = node.lines;
    edit(
      (ns) => setField(ns, node.id, { lines }),
      () => patch(node.id, { lines }),
      () => patch(node.id, { lines: old }),
    );
  };
  const changeType = (node: DocNode, type: NodeType) => {
    const old = node.type;
    // Auto-nest a freshly-typed Congregation under the nearest preceding Leader
    // sibling (same default as import + the manual indent), unless it's already
    // sitting under one. One combined edit, so a single undo reverses both.
    let target: DocNode | null = null;
    if (editorType(type) === 'response' && editorType(old) !== 'response') {
      const sibs = childrenOf(node.parentId);
      const idx = sibs.findIndex((s) => s.id === node.id);
      for (let i = idx - 1; i >= 0; i--) {
        const s = sibs[i];
        if (s && editorType(s.type) === 'spoken') {
          target = s;
          break;
        }
      }
    }
    if (target) {
      const parentId = target.id;
      const kids = childrenOf(parentId);
      const lastKid = kids[kids.length - 1];
      const orderIndex = lastKid ? lastKid.orderIndex + 1 : 1;
      const oldParent = node.parentId;
      const oldOrder = node.orderIndex;
      edit(
        (ns) => setField(ns, node.id, { type, parentId, orderIndex }),
        () => patch(node.id, { type, parentId, orderIndex }),
        () => patch(node.id, { type: old, parentId: oldParent, orderIndex: oldOrder }),
      );
    } else {
      edit(
        (ns) => setField(ns, node.id, { type }),
        () => patch(node.id, { type }),
        () => patch(node.id, { type: old }),
      );
    }
  };
  // Re-parent / re-order — the ⇥/⇤ buttons and the drag both go through here.
  const move = (node: DocNode, parentId: string | null, orderIndex: number) => {
    const oldParent = node.parentId;
    const oldOrder = node.orderIndex;
    edit(
      (ns) => setField(ns, node.id, { parentId, orderIndex }),
      () => patch(node.id, { parentId, orderIndex }),
      () => patch(node.id, { parentId: oldParent, orderIndex: oldOrder }),
    );
  };

  const descendantIds = (id: string): string[] => {
    const out: string[] = [];
    const collect = (pid: string) => {
      for (const n of nodes)
        if (n.parentId === pid && !n.deleted) {
          out.push(n.id);
          collect(n.id);
        }
    };
    collect(id);
    return out;
  };
  const del = (node: DocNode) => {
    if (!window.confirm('Delete this block? Lines nested under it are removed too.')) return;
    const ids = new Set([node.id, ...descendantIds(node.id)]);
    edit(
      (ns) => ns.map((n) => (ids.has(n.id) ? { ...n, deleted: true } : n)),
      () => api.deleteNode(docId, node.id),
      () => api.restoreNode(docId, node.id),
    );
  };

  // Create a block: await the POST so the row carries its real id, but never
  // refetch the whole document — splice the new node into the local copy.
  const create = (input: NewNodeInput) => {
    const prev = nodes;
    setError(null);
    api
      .createNode(docId, input)
      .then((created) => {
        setNodes((ns) => [...ns, created]);
        record(prev, () => api.deleteNode(docId, created.id));
      })
      .catch((e: unknown) => fail(e, prev));
  };
  // Add a block nested one level under the clicked block, as its first child —
  // so it lands directly below, indented. Building a service top-down then
  // flows naturally: rubric → + leader → + congregation. Use ⇤ to step out.
  const addBelow = (node: DocNode) => {
    const first = childrenOf(node.id)[0];
    const orderIndex = first ? first.orderIndex - 1 : 1;
    create({ type: 'spoken', parentId: node.id, orderIndex, lines: toLines('') });
  };

  // Where a block would step in to: a Congregation response nests under the
  // nearest preceding Leader sibling; any top-level line nests under the nearest
  // preceding rubric. Returns the would-be parent, or null if it can't indent.
  const indentParent = (node: DocNode): DocNode | null => {
    const sibs = childrenOf(node.parentId);
    const idx = sibs.findIndex((s) => s.id === node.id);
    if (editorType(node.type) === 'response') {
      for (let i = idx - 1; i >= 0; i--) {
        const cand = sibs[i];
        if (cand && editorType(cand.type) === 'spoken') return cand;
      }
    }
    if (node.parentId === null && node.type !== 'rubric' && node.type !== 'heading') {
      for (let i = idx - 1; i >= 0; i--) {
        if (sibs[i]?.type === 'rubric') return sibs[i] ?? null;
      }
    }
    return null;
  };

  const indent = (node: DocNode) => {
    const parent = indentParent(node);
    if (!parent) return;
    const kids = childrenOf(parent.id);
    const last = kids[kids.length - 1];
    move(node, parent.id, last ? last.orderIndex + 1 : 1);
  };
  // Step out one level: land right after the current parent among its siblings.
  const outdent = (node: DocNode) => {
    if (!node.parentId) return;
    const parent = live.find((n) => n.id === node.parentId);
    if (!parent) return;
    const gsibs = childrenOf(parent.parentId);
    const pIdx = gsibs.findIndex((s) => s.id === parent.id);
    const after = gsibs[pIdx + 1];
    move(
      node,
      parent.parentId,
      after ? mid(parent.orderIndex, after.orderIndex) : parent.orderIndex + 1,
    );
  };

  // Keyboard reorder (WCAG 2.2 AA / ux.md UX-3 — the drag path is pointer-only).
  // The ↑/↓ buttons shift a block one slot among its same-parent siblings,
  // reusing move() so persistence + undo are identical to a drag reorder. The
  // new orderIndex uses the same fractional-midpoint rule as the drop. No-op at
  // the ends — the buttons are disabled there, so this only guards re-entrancy.
  const moveUp = (node: DocNode) => {
    const order = reorderIndex(childrenOf(node.parentId), node.id, 'up');
    if (order === null) return;
    move(node, node.parentId, order);
  };
  const moveDown = (node: DocNode) => {
    const order = reorderIndex(childrenOf(node.parentId), node.id, 'down');
    if (order === null) return;
    move(node, node.parentId, order);
  };

  // Live drop position from the pointer's vertical half of the hovered row.
  const onRowDragOver = (e: DragEvent<HTMLDivElement>, node: DocNode) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragId === node.id) {
      if (dropAt) setDropAt(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const pos: 'before' | 'after' = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
    setDropAt((prev) =>
      prev && prev.id === node.id && prev.pos === pos ? prev : { id: node.id, pos },
    );
  };

  // Drop: the dragged block takes the target's level (cross-indent) and lands
  // before/after it. Structural blocks snap to top level (can't nest).
  const onDrop = () => {
    const dragged = dragId ? live.find((n) => n.id === dragId) : undefined;
    const at = dropAt;
    setDragId(null);
    setArmed(null);
    setDropAt(null);
    if (!dragged || !at) return;
    const target = live.find((n) => n.id === at.id);
    if (!target || target.id === dragged.id) return;

    let newParent = target.parentId;
    let anchor = target;
    if (isStructural(dragged.type) && newParent !== null) {
      anchor = live.find((n) => n.id === newParent) ?? target;
      newParent = null;
    }
    if (anchor.id === dragged.id) return;

    const sibs = childrenOf(newParent).filter((s) => s.id !== dragged.id);
    const aIdx = sibs.findIndex((s) => s.id === anchor.id);
    if (aIdx === -1) return;
    let newOrder: number;
    if (at.pos === 'before') {
      const prev = sibs[aIdx - 1];
      newOrder = prev ? mid(prev.orderIndex, anchor.orderIndex) : anchor.orderIndex - 1;
    } else {
      const next = sibs[aIdx + 1];
      newOrder = next ? mid(anchor.orderIndex, next.orderIndex) : anchor.orderIndex + 1;
    }
    if (newParent === dragged.parentId && newOrder === dragged.orderIndex) return;
    move(dragged, newParent, newOrder);
  };

  return (
    <div className="block-editor">
      <div className="be-bar">
        <p className="be-help muted">
          Editing the service text — drag a block by its handle to reorder or re-nest, or use the
          move up / move down buttons on each block; changes save as you go.
        </p>
        <button
          type="button"
          className="be-undo"
          onClick={undo}
          disabled={undoStack.length === 0}
          title="Undo the last change"
        >
          <span className="be-undo-icon" aria-hidden="true">
            ↩
          </span>
          Undo{undoStack.length ? ` (${undoStack.length})` : ''}
        </button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      {rows.map(({ node, depth }) => {
        const canIndent = indentParent(node) !== null;
        // Position among same-parent siblings — drives the ↑/↓ boundary disable.
        const sibs = childrenOf(node.parentId);
        const sibIdx = sibs.findIndex((s) => s.id === node.id);
        const canMoveUp = sibIdx > 0;
        const canMoveDown = sibIdx >= 0 && sibIdx < sibs.length - 1;
        const dropCls =
          dropAt?.id === node.id ? (dropAt.pos === 'before' ? ' drop-before' : ' drop-after') : '';
        return (
          <div
            key={node.id}
            className={`be-row${depth ? ' child' : ''}${dragId === node.id ? ' dragging' : ''}${dropCls}`}
            style={{ marginLeft: `${rowIndentRem(node.type, depth)}rem` }}
            draggable={armed === node.id}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', node.id);
              setDragId(node.id);
            }}
            onDragEnd={() => {
              setDragId(null);
              setArmed(null);
              setDropAt(null);
            }}
            onDragOver={(e) => onRowDragOver(e, node)}
            onDrop={(e) => {
              e.preventDefault();
              onDrop();
            }}
          >
            <div className="be-head">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- mousedown only arms the HTML5 drag on the row; there is no click action to mirror on keyboard */}
              <span
                className="be-handle"
                title="Drag to reorder"
                onMouseDown={() => setArmed(node.id)}
                onMouseUp={() => setArmed(null)}
              >
                ⠿
              </span>
              <select
                className="be-type"
                value={editorType(node.type)}
                onChange={(e) => changeType(node, e.target.value as NodeType)}
              >
                {EDITOR_TYPES.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.label}
                  </option>
                ))}
              </select>
              <div className="be-controls">
                {/* Keyboard reorder path (ux.md UX-3): the drag handle above is
                    pointer-only, so these give the same reorder without a mouse.
                    The row key is node.id, so React keeps focus on this button
                    across the re-order until it reaches an end and disables. */}
                <button
                  type="button"
                  className="be-btn"
                  aria-label="Move block up"
                  title="Move up"
                  disabled={!canMoveUp}
                  onClick={() => moveUp(node)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="be-btn"
                  aria-label="Move block down"
                  title="Move down"
                  disabled={!canMoveDown}
                  onClick={() => moveDown(node)}
                >
                  ↓
                </button>
                {canIndent && (
                  <button
                    type="button"
                    className="be-btn"
                    title="Nest under rubric"
                    onClick={() => indent(node)}
                  >
                    ⇥
                  </button>
                )}
                {depth > 0 && (
                  <button
                    type="button"
                    className="be-btn"
                    title="Un-nest"
                    onClick={() => outdent(node)}
                  >
                    ⇤
                  </button>
                )}
                <button
                  type="button"
                  className="be-btn"
                  title="Add a block nested below"
                  onClick={() => addBelow(node)}
                >
                  +
                </button>
                <button
                  type="button"
                  className="be-btn danger"
                  title="Delete block"
                  onClick={() => del(node)}
                >
                  🗑
                </button>
              </div>
            </div>
            <LineEditor
              key={`${node.id}:${JSON.stringify(node.lines)}`}
              lines={node.lines}
              bold={editorType(node.type) === 'response'}
              onSave={(lines) => saveLines(node, lines)}
            />
          </div>
        );
      })}

      {rows.length === 0 && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            create({
              type: 'heading',
              parentId: null,
              orderIndex: 1,
              lines: toLines('New section'),
            })
          }
        >
          Add first block
        </button>
      )}

      {/* Free-text notes — after the service. Inline bold/italic only. Shown to
          every reader below the service and in the printed copy. */}
      <section className="be-notes" aria-label="Notes">
        <h2 className="be-notes-head">Notes</h2>
        <p className="be-notes-help muted">
          Explanatory notes shown to all readers below the service (and in the printed copy) — for
          ongoing explanation of elements, beyond the rationale on the right. Bold and italic only;
          saved as you go.
        </p>
        <LineEditor key={`notes:${JSON.stringify(notes)}`} lines={notes} onSave={saveNotes} />
      </section>
    </div>
  );
}
